<script setup lang="ts">
import AcceptedCard from './history/AcceptedCard.vue'
import AnsweredCard from './history/AnsweredCard.vue'
import ContactedCard from './history/ContactedCard.vue'
import ExpiredCard from './history/ExpiredCard.vue'
import SentCard from './history/SentCard.vue'
import SharedCard from './history/SharedCard.vue'

import OrangeTitleWithSpacedContent from '~/components/ui/Sections/OrangeTitleWithSpacedContent.vue'

import { useLanguage } from '~/composables/useLanguage'

import { formatDateToDefaultDisplay } from '~/utils/DateDisplay'

import type { Influencer } from '~/types/influencer'
import type { InfluencerInteraction } from '~/types/influencerInteraction'

type Props = {
  influencer: Influencer
  interactions: InfluencerInteraction[]
}

const props = defineProps<Props>()
const { getCurrentLanguage } = useLanguage()

const answeredCount = computed<number>(() => {
  return props.interactions.reduce<number>((acc, submission) => {
    if (!submission.answer || !submission.answer.feedback?.length) return acc
    return acc + 1
  }, 0)
})

const acceptedCount = computed<number>(
  () =>
    props.interactions.filter((s) =>
      s.answer?.decisions.some((e) => e.accepted_status === true),
    ).length,
)

const sharedCount = computed<number>(() => {
  return props.interactions.reduce((acc, submission) => {
    if (!submission.answer?.link?.length) return acc
    return acc + 1
  }, 0)
})

const contactedCount = computed<number>(() => {
  return props.interactions.reduce<number>((acc, submission) => {
    if (!submission.answer?.decisions?.length) return acc
    if (!submission.answer.decisions.some((e) => e.contacted_status === true))
      return acc
    return acc + 1
  }, 0)
})

const expiredCount = computed<number>(() => {
  return props.interactions.reduce<number>((acc, interaction) => {
    if (interaction.status === 'Ex') return acc + 1
    return acc
  }, 0)
})

const firstSubmissionDate = computed<string | undefined>(() => {
  if (!props.interactions.length) return
  const oldestSubmission = props.interactions.toSorted(
    (a, b) => new Date(b.date_sent).getTime() - new Date(a.date_sent).getTime(),
  )[props.interactions.length - 1]

  if (!oldestSubmission) return
  return formatDateToDefaultDisplay(
    oldestSubmission.date_sent,
    getCurrentLanguage(),
  )
})
</script>

<template>
  <OrangeTitleWithSpacedContent>
    <template #title>{{
      $t('influencer.profile.slug.interaction.history.history00')
    }}</template>
    <template #content>
      <SentCard
        v-if="interactions.length && firstSubmissionDate"
        :sent-count="interactions.length"
        :first-submission-date="firstSubmissionDate"
      />
      <AnsweredCard
        v-if="answeredCount"
        :name="influencer.entity"
        :answered-count="answeredCount"
      />
      <AcceptedCard
        v-if="acceptedCount"
        :name="influencer.entity"
        :accepted-count="acceptedCount"
      />
      <SharedCard
        v-if="sharedCount"
        :name="influencer.entity"
        :shared-count="sharedCount"
      />
      <ContactedCard
        v-if="contactedCount"
        :name="influencer.entity"
        :contacted-count="contactedCount"
      />
      <ExpiredCard
        v-if="expiredCount"
        :name="influencer.entity"
        :expired-count="expiredCount"
      />
    </template>
  </OrangeTitleWithSpacedContent>
</template>
